import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DropdownType, SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { TranslateService } from '@ngx-translate/core';
import { ShipyardService } from '@service/shipyard.service';
import { VersionsHistoryService } from '@service/versions-history.service';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { Graph } from 'icreate/common/models/graphs';
import { Mimic } from 'icreate/common/models/mimic';
import { PublishedVersion } from 'icreate/common/models/versions-history';
import { Vessel } from 'icreate/common/models/vessel';

@Component({
    selector: 'app-print-dialog',
    templateUrl: './print-dialog.component.html',
    styleUrl: './print-dialog.component.css',
    standalone: false,
})
export class PrintDialogComponent implements OnInit {
    readonly DropdownType = DropdownType;

    readonly CHANNELS_PRINT = PrintPageType.CHANNEL_SHEET;
    readonly SINGLE_MIMIC_PRINT = PrintPageType.SINGLE_MIMIC;
    readonly SENSOR_CABLE_TAG = PrintPageType.SENSOR_CABLE_TAG;
    readonly GRAPHS_PRINT = PrintPageType.GRAPHS;
    readonly ALARM_GROUP_PRINT = PrintPageType.ALARM_GROUPS;
    readonly REPOSE_GROUP_PRINT = PrintPageType.REPOSE_GROUPS;
    readonly SENSOR_FU_PRINT = PrintPageType.SENSOR_FU_PRINT;

    readonly VERSIONS_FIELD = 'versions';
    readonly OPTIONS_FIELD = 'options';

    pageType: PrintPageType;
    mimicName: string;
    vesselName: string;
    iAPIVesselId: string;
    iAPIShipyardId: string;

    versionsList: PublishedVersion[];

    selectedGroups: Set<string> = new Set();
    selectedMimics: Set<string> = new Set();
    selectedFcus: Set<number> = new Set();
    selectedGraphs: Set<string> = new Set();
    selectedFus: Set<number> = new Set();

    selectedFcuNum: number | null = null;
    selectedFcu: SimpleDropdownOption | null = null;

    printAll: boolean = true;
    compareVersions: boolean = true;
    isLoading: boolean = false;
    hasOldVersions: boolean = false;

    versionToCompare: string;
    dropdownOptions: any[] = [];
    selectedOptionItems: any[] = [];

    notFromMain: boolean = false;
    versionsDropdownOptions: { value: string; string: string }[];
    fcuDropdownOptions: { value: any; string: string }[];

    constructor(
        private versionHistoryService: VersionsHistoryService,
        private dialogRef: MatDialogRef<PrintDialogComponent>,
        private shipyardService: ShipyardService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            pageType: PrintPageType;
            mimicName: string;
            shipyardId: string;
            iAPIVesselId: string;
            vessel: Vessel;
            options: any[];
            extraPreField?: string;
            extraPreOptions?: any[];
            notMain?: boolean;
        }
    ) {}

    async ngOnInit() {
        this.isLoading = true;
        if (!this.data.pageType) this.cancel();
        this.pageType = this.data.pageType;

        if (this.data.vessel) this.vesselName = this.data.vessel.name;
        if (this.data.mimicName) this.mimicName = this.data.mimicName;
        if (this.data.iAPIVesselId) this.iAPIVesselId = this.data.iAPIVesselId;
        if (this.data.notMain) this.notFromMain = this.data.notMain;

        if (this.pageType == PrintPageType.SINGLE_MIMIC) this.printAll = false;

        const shipyardList = await this.shipyardService.getList();
        if (!shipyardList || shipyardList.length == 0) return;
        shipyardList.forEach(shipyard => {
            if (shipyard.id == this.data.shipyardId) {
                this.iAPIShipyardId = shipyard.iApiCompanyId;
            }
        });

        if (!this.iAPIShipyardId || !this.iAPIVesselId) return;

        this.versionsList = await this.versionHistoryService.getVersionsList(this.iAPIShipyardId, this.iAPIVesselId);
        this.versionToCompare = this.versionsList?.[0]?.name ?? '';
        this.compareVersions = this.versionsList?.[0]?.name ? true : false;
        this.hasOldVersions = this.versionsList?.[0]?.name ? true : false;
        if (this.isPrintWithUpdatesDisabled) this.compareVersions = false;

        this.updateDropdownOptions();
        this.updateSelectedOptionItems();
        this.updateVersionsDropdownOptions();
        if (this.pageType == PrintPageType.SENSOR_FU_PRINT) this.updateFcuDropdownOptions();

        this.isLoading = false;
        this.cdr.markForCheck();
    }

    cancel() {
        if (this.notFromMain) this.dialogRef.close();
        else this.dialogRef.close({ cancel: true });
    }

    back() {
        this.dialogRef.close();
    }

    get isContainerSelectionsDisabled(): boolean {
        return this.pageType === this.SENSOR_FU_PRINT && !this.selectedFcu;
    }

    isPrintDisabled(): boolean {
        if (this.compareVersions && !this.versionToCompare) return true;

        if (this.pageType === PrintPageType.SENSOR_FU_PRINT) {
            if (this.selectedFcuNum == null) return true;
            if (!this.printAll && this.selectedFus.size === 0) return true;
            return false;
        }

        if (
            !this.printAll &&
            this.pageType != PrintPageType.SINGLE_MIMIC &&
            this.selectedGroups.size === 0 &&
            this.selectedMimics.size === 0 &&
            this.selectedFcus.size === 0 &&
            this.selectedGraphs.size === 0
        ) {
            return true;
        }

        return false;
    }

    print() {
        if (this.isPrintDisabled()) return;
        const setListIds = this.getSetIds();
        this.dialogRef.close({
            printType: this.compareVersions ? PrintType.UPDATES : PrintType.NO_UPDATES,
            iAPIShipyardlId: this.iAPIShipyardId,
            version: this.versionToCompare,
            setListIds: setListIds,
            fcuNum: this.selectedFcuNum,
        });
    }

    getSetIds() {
        if (this.pageType == PrintPageType.SINGLE_MIMIC) return new Set();
        else if (this.pageType == PrintPageType.CHANNEL_SHEET) {
            if (this.printAll) this.selectedGroups = new Set(this.data?.options?.map(g => g.id));
            return this.selectedGroups;
        } else if (this.pageType == PrintPageType.ALL_MIMICS) {
            if (this.printAll) this.selectedMimics = new Set(this.data?.options?.map(m => m.id));
            return this.selectedMimics;
        } else if (this.pageType == PrintPageType.SENSOR_CABLE_TAG) {
            if (this.printAll) this.selectedFcus = new Set(this.data?.options);
            return this.selectedFcus;
        } else if (this.pageType == PrintPageType.GRAPHS) {
            if (this.printAll) this.selectedGraphs = new Set(this.data?.options?.map(m => m.id));
            return this.selectedGraphs;
        } else if (this.pageType == PrintPageType.SENSOR_FU_PRINT) {
            return this.printAll ? new Set(this.getListOptions()) : new Set(this.selectedFus);
        }
    }

    getListOptions() {
        if (this.pageType == PrintPageType.SENSOR_FU_PRINT) {
            if (this.selectedFcuNum == null) return [];
            const found = this.data?.extraPreOptions?.find((item: any) => item && item.fcuNum === this.selectedFcuNum);
            return found?.fuList ?? [];
        }
        return this.data?.options || [];
    }

    selectOption(option: any) {
        if (this.pageType == PrintPageType.ALL_MIMICS) {
            if (this.selectedMimics.has(option.id)) this.selectedMimics.delete(option.id);
            else this.selectedMimics.add(option.id);
        } else if (this.pageType == PrintPageType.CHANNEL_SHEET) {
            if (this.selectedGroups.has(option.id)) this.selectedGroups.delete(option.id);
            else this.selectedGroups.add(option.id);
        } else if (this.pageType == PrintPageType.SENSOR_CABLE_TAG) {
            if (this.selectedFcus.has(option)) this.selectedFcus.delete(option);
            else this.selectedFcus.add(option);
        } else if (this.pageType == PrintPageType.GRAPHS) {
            if (this.selectedGraphs.has(option.id)) this.selectedGraphs.delete(option.id);
            else this.selectedGraphs.add(option.id);
        } else if (this.pageType == PrintPageType.SENSOR_FU_PRINT) {
            if (this.selectedFus.has(option)) this.selectedFus.delete(option);
            else this.selectedFus.add(option);
        }
        this.updateSelectedOptionItems();
    }

    isOptionSelected(option: any) {
        if (this.pageType == PrintPageType.ALL_MIMICS) return this.selectedMimics.has(option.id);
        else if (this.pageType == PrintPageType.CHANNEL_SHEET) return this.selectedGroups.has(option.id);
        else if (this.pageType == PrintPageType.SENSOR_CABLE_TAG) return this.selectedFcus.has(option);
        else if (this.pageType == PrintPageType.GRAPHS) return this.selectedGraphs.has(option.id);
        else if (this.pageType == PrintPageType.SENSOR_FU_PRINT) return this.selectedFus.has(option);
    }

    getOptionDisplayName(option: any) {
        if (this.pageType == PrintPageType.ALL_MIMICS) return (option as Mimic).name;
        else if (this.pageType == PrintPageType.SENSOR_CABLE_TAG) return 'FCU ' + option;
        else if (this.pageType == PrintPageType.CHANNEL_SHEET)
            return (option as ChannelGroup).rowNum + ' ' + (option as ChannelGroup).displayName;
        else if (this.pageType == PrintPageType.GRAPHS) return (option as Graph).title;
        else if (this.pageType == PrintPageType.SENSOR_FU_PRINT) return 'FU ' + option;
    }

    isDisabledField(field): boolean {
        if (field == this.VERSIONS_FIELD) {
            return !this.compareVersions || !this.hasOldVersions;
        }
        if (field == this.OPTIONS_FIELD) {
            if (this.pageType == PrintPageType.SENSOR_FU_PRINT) {
                return this.printAll || this.selectedFcuNum == null;
            }
            return this.printAll || this.pageType == PrintPageType.SINGLE_MIMIC;
        }
        return false;
    }

    updateFcuDropdownOptions() {
        this.fcuDropdownOptions =
            this.data?.options?.map(option => ({
                value: option,
                string: 'FCU ' + option,
            })) || [];
    }

    onFcuSelected(option: SimpleDropdownOption): void {
        this.selectedFcu = option;
        this.selectedFcuNum = option.value;
        this.selectedFus.clear();
        this.updateDropdownOptions();
        this.updateSelectedOptionItems();
        this.cdr.markForCheck();
    }

    compareVersionRadioChanged(shouldCompare: boolean): void {
        if (shouldCompare && this.isPrintWithUpdatesDisabled) {
            return;
        }

        this.compareVersions = shouldCompare;
        this.cdr.markForCheck();
    }

    printAllRadioClicked(event, shouldPrintAll): void {
        this.printAll = shouldPrintAll;
        if (shouldPrintAll && this.pageType === PrintPageType.SENSOR_FU_PRINT) {
            this.selectedFus.clear();
        }
        this.updateSelectedOptionItems();
        this.cdr.markForCheck();
    }

    getTranslationString(baseString) {
        let pageType = 'mimics';
        if (this.pageType == this.CHANNELS_PRINT) pageType = 'channel';
        else if (this.pageType == this.SENSOR_CABLE_TAG) pageType = 'sensor-cable-tag';
        else if (this.pageType == this.GRAPHS_PRINT) pageType = 'graphs';
        else if (this.pageType == this.ALARM_GROUP_PRINT) pageType = 'alarm-group';
        else if (this.pageType == this.REPOSE_GROUP_PRINT) pageType = 'repose-group';
        else if (this.pageType == this.SENSOR_FU_PRINT) pageType = 'sensor-fu';
        return baseString + pageType;
    }

    updateDropdownOptions() {
        this.dropdownOptions = this.getListOptions().map(option => ({
            value: option,
            string: this.getOptionDisplayName(option),
        }));
    }

    private updateSelectedOptionItems() {
        this.selectedOptionItems = this.getListOptions().filter(option => this.isOptionSelected(option));
    }

    updateVersionsDropdownOptions() {
        this.versionsDropdownOptions = (this.versionsList ?? []).map(version => ({
            value: version.name,
            string: version.name,
        }));
    }

    get isPrintWithUpdatesDisabled(): boolean {
        return (
            !this.hasOldVersions ||
            this.pageType == this.SENSOR_CABLE_TAG ||
            this.pageType == this.SENSOR_FU_PRINT ||
            this.pageType == this.ALARM_GROUP_PRINT ||
            this.pageType == this.REPOSE_GROUP_PRINT
        );
    }

    onDropdownOptionsSelected(selected: any[]): void {
        const previouslySelectedIds = new Set(this.selectedOptionItems.map(item => item.id || item));
        const newlySelectedIds = new Set(selected.map(item => item.id || item));

        for (const option of this.selectedOptionItems) {
            const id = option.id || option;
            if (!newlySelectedIds.has(id)) {
                this.selectOption(option);
            }
        }

        for (const option of selected) {
            const id = option.id || option;
            if (!previouslySelectedIds.has(id)) {
                this.selectOption(option);
            }
        }
        this.updateSelectedOptionItems();
    }

    onVersionDropdownSelected(option: SimpleDropdownOption): void {
        this.versionToCompare = option.value;
    }

    get shouldShowGroupSelector(): boolean {
        return this.pageType !== PrintPageType.ALARM_GROUPS && this.pageType !== PrintPageType.REPOSE_GROUPS;
    }
}

export enum PrintType {
    UNDEFINED = 'undefined',
    UPDATES = 'updates',
    NO_UPDATES = 'no-updates',
}

export enum PrintPageType {
    UNDEFINED = 'undefined',
    CHANNEL_SHEET = 'channel-sheet',
    SINGLE_MIMIC = 'single-mimic',
    ALL_MIMICS = 'all-mimics',
    SENSOR_CABLE_TAG = 'sensor-cable-tag',
    GRAPHS = 'graphs',
    REPOSE_GROUPS = 'repose-groups',
    ALARM_GROUPS = 'alarm-groups',
    SENSOR_FU_PRINT = 'sensor-fu-print',
}
